/**
 * blog-authoring.ts — event payloads for the AI-authoring flow (P3).
 *
 * Flow (spec 2026-06-25-wandero-blog-multilang-ai-authoring §P3):
 *   admin emit_event('blog.authoring.requested', {...}, {correlation_id})
 *     → worker handler dispatches to dotclaude content-writer (agent mode,
 *       webhook callback — NOT poll-based), emits 'blog.authoring.dispatched'
 *       and returns (no blocking wait).
 *     → api POST /v1/webhooks/blog-authoring (signed) verifies + writes
 *       blog_posts_i18n[source_lang].body_html, emits
 *       'blog.authoring.completed' so admin Realtime (subscribed by
 *       correlation_id) flips generating→done.
 *
 * These are the *reactor* event payloads (events-table), distinct from the
 * dotclaude wire-level webhook payload (see fixtures/dotclaude-blog/README.md
 * for the `task.complete` webhook contract shape).
 */

import { z } from 'zod';

export const BLOG_AUTHORING_EVENTS = {
  REQUESTED: 'blog.authoring.requested',
  DISPATCHED: 'blog.authoring.dispatched',
  COMPLETED: 'blog.authoring.completed',
  FAILED: 'blog.authoring.failed',
} as const;

// ---------------------------------------------------------------------------
// blog.authoring.requested — admin → worker
// ---------------------------------------------------------------------------

export const blogAuthoringRequestedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  /** Free-form authoring brief typed by the admin user. */
  brief: z.string().min(1).max(8000),
  /** Locale the draft is authored in (must match blog_posts.source_lang). */
  source_lang: z.string().min(2).max(10),
  /** Reactor correlation_id, echoed back so admin Realtime can match the run. */
  correlation_id: z.string().min(1),
});

export type BlogAuthoringRequestedPayload = z.infer<typeof blogAuthoringRequestedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.authoring.dispatched — worker → (admin, informational)
//
// Emitted right after the dotclaude /v1/tasks agent dispatch succeeds.
// Dispatch-only ack: the handler does NOT poll/block for the result — the
// webhook completes the round-trip via blog.authoring.completed.
// ---------------------------------------------------------------------------

export const blogAuthoringDispatchedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  /** dotclaude task id, for cross-referencing during debugging. */
  task_id: z.string().min(1).optional(),
});

export type BlogAuthoringDispatchedPayload = z.infer<typeof blogAuthoringDispatchedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.authoring.completed — api (webhook receiver) → admin (Realtime)
// ---------------------------------------------------------------------------

export const blogAuthoringCompletedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  source_lang: z.string().min(2).max(10),
});

export type BlogAuthoringCompletedPayload = z.infer<typeof blogAuthoringCompletedPayloadSchema>;

// ---------------------------------------------------------------------------
// blog.authoring.failed — worker or api → admin (Realtime)
// ---------------------------------------------------------------------------

export const blogAuthoringFailedPayloadSchema = z.object({
  post_id: z.number().int().positive(),
  correlation_id: z.string().min(1),
  error: z.string(),
});

export type BlogAuthoringFailedPayload = z.infer<typeof blogAuthoringFailedPayloadSchema>;
